import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';

function ComplianceDashboard() {
  const [auditStats, setAuditStats] = useState(null);
  const [complianceData, setComplianceData] = useState(null);


  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      // Fetch audit stats
      const auditResponse = await axiosClient.get('/audit-logs/stats');
      setAuditStats(auditResponse.data);

      // Fetch real-time compliance status
      const complianceResponse = await axiosClient.get('/compliance/status');
      setComplianceData(complianceResponse.data);
      
    } catch (err) {
      console.error('Error fetching compliance data:', err);
    } finally {
      // loading state removed
    }
  };

  // Fallback static data for System Security category (not dynamically checked yet)
  const systemSecurityFallback = {
    category: 'System Security',
    items: [
      { name: 'CORS Configuration', status: 'implemented', description: 'Configured for local development' },
      { name: 'HTTPS/TLS', status: 'pending', description: 'Required for production deployment' },
      { name: 'Security Headers', status: 'pending', description: 'CSP, HSTS, X-Frame-Options' },
      { name: 'Input Validation', status: 'partial', description: 'Basic validation implemented' },
      { name: 'SQL Injection Protection', status: 'implemented', description: 'Using SQLAlchemy ORM' },
      { name: 'XSS Protection', status: 'partial', description: 'React provides some protection' }
    ]
  };

  // Use dynamic data if available, otherwise use fallback
  const complianceChecklist = complianceData 
    ? [...complianceData.categories, systemSecurityFallback]
    : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'implemented': return { bg: '#d4edda', color: '#155724' };
      case 'partial': return { bg: '#fff3cd', color: '#856404' };
      case 'pending': return { bg: '#f8d7da', color: '#721c24' };
      default: return { bg: '#e9ecef', color: '#6c757d' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'implemented': return '';
      case 'partial': return '';
      case 'pending': return '';
      default: return '';
    }
  };

  const calculateProgress = (items) => {
    const implemented = items.filter(i => i.status === 'implemented').length;
    const partial = items.filter(i => i.status === 'partial').length;
    const total = items.length;
    return Math.round(((implemented + (partial * 0.5)) / total) * 100);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '10px', color: '#333' }}>Compliance Dashboard</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Monitor compliance status with security standards and regulations
      </p>

      {/* Overview Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '40px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Overall Compliance</div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {complianceData?.overall_completion || 0}%
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>
            Real-time system status
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            {complianceChecklist[0]?.category || 'Security Controls'}
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {complianceChecklist[0]?.completion_percentage || calculateProgress(complianceChecklist[0]?.items || [])}%
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>
            {complianceChecklist[0]?.items.filter(i => i.status === 'implemented').length || 0} of {complianceChecklist[0]?.items.length || 0} implemented
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            {complianceChecklist[1]?.category || 'GDPR Compliance'}
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            {complianceChecklist[1]?.completion_percentage || calculateProgress(complianceChecklist[1]?.items || [])}%
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>
            {complianceChecklist[1]?.items.filter(i => i.status === 'implemented').length || 0} of {complianceChecklist[1]?.items.length || 0} implemented
          </div>
        </div>

        {auditStats && (
          <div style={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            padding: '25px',
            borderRadius: '12px',
            color: 'white',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Audit Events</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
              {auditStats.total_logs}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              Security actions logged
            </div>
          </div>
        )}
      </div>

      {/* Compliance Checklist */}
      {complianceChecklist.map((category, idx) => (
        <div key={idx} style={{ marginBottom: '30px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderBottom: '2px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>{category.category}</h2>
                <div style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  background: getStatusColor(
                    calculateProgress(category.items) === 100 ? 'implemented' :
                    calculateProgress(category.items) >= 50 ? 'partial' : 'pending'
                  ).bg,
                  color: getStatusColor(
                    calculateProgress(category.items) === 100 ? 'implemented' :
                    calculateProgress(category.items) >= 50 ? 'partial' : 'pending'
                  ).color,
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {calculateProgress(category.items)}% Complete
                </div>
              </div>
            </div>

            <div style={{ padding: '0' }}>
              {category.items.map((item, itemIdx) => (
                <div
                  key={itemIdx}
                  style={{
                    padding: '20px',
                    borderBottom: itemIdx < category.items.length - 1 ? '1px solid #e9ecef' : 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '15px'
                  }}
                >
                  <div style={{ fontSize: '24px', flexShrink: 0 }}>
                    {getStatusIcon(item.status)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '4px'
                    }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {item.description}
                    </div>
                  </div>
                  <div>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize',
                      background: getStatusColor(item.status).bg,
                      color: getStatusColor(item.status).color
                    }}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Action Items */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        padding: '25px',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#333' }}>
          Recommended Actions
        </h2>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div style={{
            padding: '15px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px'
          }}>
            <div style={{ fontWeight: '600', color: '#856404', marginBottom: '4px' }}>
              Complete Legal Documentation
            </div>
            <div style={{ fontSize: '14px', color: '#856404' }}>
              Fill in Privacy Policy and Terms of Service with actual content for legal compliance
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px'
          }}>
            <div style={{ fontWeight: '600', color: '#155724', marginBottom: '4px' }}>
               MFA Implemented for All Accounts
            </div>
            <div style={{ fontSize: '14px', color: '#155724' }}>
              TOTP-based multi-factor authentication is now mandatory for all user accounts with session management and rate limiting
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: '#d1ecf1',
            border: '1px solid #17a2b8',
            borderRadius: '8px'
          }}>
            <div style={{ fontWeight: '600', color: '#0c5460', marginBottom: '4px' }}>
              Create ISO 27001 Documentation
            </div>
            <div style={{ fontSize: '14px', color: '#0c5460' }}>
              Document access controls, risk assessments, and incident response procedures
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px'
          }}>
            <div style={{ fontWeight: '600', color: '#155724', marginBottom: '4px' }}>
              Add Session Management
            </div>
            <div style={{ fontSize: '14px', color: '#155724' }}>
              Implement token refresh and session tracking for better security control
            </div>
          </div>
        </div>
      </div>

      {/* Audit Log Summary */}
      {auditStats && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          padding: '25px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#333' }}>
            Audit Log Summary
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            {Object.entries(auditStats.actions || {}).map(([action, count]) => (
              <div key={action} style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', marginBottom: '8px' }}>
                  {action} Actions
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
                  {count}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ComplianceDashboard;
